import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import { USE_FILE_UPLOAD } from 'features'
import find from 'lodash/fp/find'
import CandidateTutorial from 'components/candidate-tutorials/candidate-tutorial'
import { mapLanguageToComponentProps } from 'interview/i18n'
import {
  completeVideo,
  saveDraft,
  removeDraft,
} from 'interview/actions/candidate'
import {
  completeVideo as completeMessage,
  saveDraft as saveMessageDraft,
  removeDraft as removeMessageDraft,
} from 'interview/actions/message'
import getCustomSupport from 'interview/selectors/custom-support'
import QuestionResponseDialogue from 'interview/components/question-response-dialogue'
import * as analytics from 'analytics/performance'
import { enableResponseIndividualSubmit } from 'flags'
import { getCandidateInfoForAmplitude } from 'selectors'

const propTypes = {
  dispatch: PropTypes.func.isRequired,
  candidate: PropTypes.shape({
    email: PropTypes.string,
    _id: PropTypes.string,
    uuid: PropTypes.string.isRequired,
    videoResponses: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    personalQuestions: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  questions: PropTypes.arrayOf(PropTypes.object).isRequired,
  globalRecorder: PropTypes.string,
  customSupport: PropTypes.shape({
    companyName: PropTypes.string,
    type: PropTypes.string,
    value: PropTypes.string,
  }),
  interviewCompanyData: PropTypes.shape({
    companyId: PropTypes.string,
    companyName: PropTypes.string,
  }),
  disableResponse: PropTypes.bool,
}

const defaultProps = {
  globalRecorder: '',
  customSupport: undefined,
  interviewCompanyData: {},
  disableResponse: false,
}

const mapStateToProps = state => ({
  globalRecorder: state.global.recorder,
  customSupport: getCustomSupport(state),
})
export class InterviewQuestions extends Component {
  constructor(props) {
    super(props)

    this.state = {
      eventData: undefined,
    }

    this.onVideoChange = this.onVideoChange.bind(this)
    this.onVideoSubmit = this.onVideoSubmit.bind(this)
    this.onVideoRemove = this.onVideoRemove.bind(this)
  }

  componentDidMount() {
    const { candidate, interviewCompanyData } = this.props
    const candidateInfo = getCandidateInfoForAmplitude(candidate)
    const companyAndInterviewInfo =
      interviewCompanyData && interviewCompanyData.companyId
        ? interviewCompanyData
        : {}

    this.setState({
      eventData: {
        ...candidateInfo,
        ...companyAndInterviewInfo,
      },
    })
  }

  /**
   * When video is changed dispatch changes to backend
   */

  onVideoChange(questionId, context) {
    const { candidate, dispatch, globalRecorder } = this.props
    const { eventData } = this.state
    const videoResponse = find({ questionId })(candidate.videoResponses)

    const payload = {
      isMobile: USE_FILE_UPLOAD || globalRecorder === 'upload',
      uuid: candidate.uuid,
      response: videoResponse,
    }

    if (context && context === 'message') {
      dispatch(
        saveMessageDraft({
          context: 'candidate',
          ...payload,
        })
      )
    } else {
      dispatch(saveDraft(payload))
    }

    analytics.onChangeEvent({
      locationId: 'interviewPage',
      id: 'video',
      properties: {
        ...eventData,
        recorder: videoResponse.recorder,
        hasBackground: !!(
          videoResponse.background || videoResponse.recorder === 'mediarecorder' || videoResponse.recorder === 'mediarecorder-bg'
        ),
      },
    })
  }

  /**
   * When user submits single video, disabled when enableResponseIndividualSubmit is false
   */
  onVideoSubmit(questionId, context) {
    const { candidate, dispatch } = this.props
    const { eventData } = this.state
    const videoResponse = find({ questionId })(candidate.videoResponses)

    const payload = {
      uuid: candidate.uuid,
      response: videoResponse,
    }

    if (context && context === 'message') {
      dispatch(
        completeMessage({
          context: 'candidate',
          ...payload,
        })
      )
    } else {
      dispatch(completeVideo(payload))
    }

    analytics.onClickEvent({
      locationId: 'interviewPage',
      id: 'videoSubmitButton',
      properties: {
        ...eventData,
      },
    })
  }

  /**
   * When user removes draft
   */
  onVideoRemove(questionId, context) {
    const { candidate, dispatch } = this.props
    const { candidateInfo, companyAndInterviewInfo } = this.state
    const videoResponse = find({ questionId })(candidate.videoResponses)

    const payload = {
      uuid: candidate.uuid,
      response: videoResponse,
    }

    if (context && context === 'message') {
      dispatch(
        removeMessageDraft({
          context: 'candidate',
          ...payload,
        })
      )
    } else {
      dispatch(removeDraft(payload))
    }

    analytics.onChangeEvent({
      locationId: 'interviewPage',
      id: 'videoDraftRemoveButton',
      properties: {
        ...candidateInfo,
        ...companyAndInterviewInfo,
      },
    })
  }

  render() {
    const {
      candidate: { uuid, videoResponses, personalQuestions },
      questions,
      globalRecorder,
      customSupport,
      disableResponse,
    } = this.props
    const { eventData } = this.state

    const enableResponseSubmit = enableResponseIndividualSubmit()
    return (
      <div className="interview-questions">
        <CandidateTutorial numbers steps={[0, 1]} className="first" />
        <QuestionResponseDialogue
          questions={questions}
          responses={videoResponses}
          useFileUpload={USE_FILE_UPLOAD || globalRecorder === 'upload'}
          uuid={uuid}
          onVideoSubmit={this.onVideoSubmit}
          onVideoChange={this.onVideoChange}
          onVideoRemove={this.onVideoRemove}
          enableSubmit={enableResponseSubmit}
          locationId="interviewPage"
          videoEventProps={eventData}
          disableResponse={disableResponse}
        />
        <QuestionResponseDialogue
          questions={personalQuestions}
          responses={videoResponses}
          useFileUpload={USE_FILE_UPLOAD || globalRecorder === 'upload'}
          uuid={uuid}
          onVideoSubmit={questionId =>
            this.onVideoSubmit(questionId, 'message')
          }
          onVideoChange={questionId =>
            this.onVideoChange(questionId, 'message')
          }
          onVideoRemove={questionId =>
            this.onVideoRemove(questionId, 'message')
          }
          enableSubmit={enableResponseSubmit}
          locationId="interviewPage"
          videoEventProps={eventData}
          disableResponse={disableResponse}
        />
        <CandidateTutorial
          numbers
          steps={[2, 3]}
          className="second"
          customSupport={customSupport}
        />
      </div>
    )
  }
}

InterviewQuestions.propTypes = propTypes
InterviewQuestions.defaultProps = defaultProps

export default connect(mapLanguageToComponentProps(mapStateToProps))(
  InterviewQuestions
)
